import React, { useState, useEffect } from 'react';
import { VCData } from './types/vc';
import { VCModal } from './components/VCModal';
import { FiltersSidebar } from './components/filters/FiltersSidebar';
import { Filters } from './types/filters';
import { Header } from './components/Header/Header';
import { Results } from './components/Results/Results';
import { LoadingSpinner } from './components/shared/LoadingSpinner';
import { ErrorMessage } from './components/shared/ErrorMessage';
import { initializeAnalytics } from './analytics/utils/init';
import { trackFilterChange, trackModalInteraction, trackVCInteraction } from './analytics/utils/tracking';
import { usePageView, useSearchTracking } from './analytics/hooks/useAnalytics';

// Initialize analytics
initializeAnalytics();

export function App() {
  usePageView();
  const [vcData, setVcData] = useState<VCData[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedVC, setSelectedVC] = useState<VCData | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [filters, setFilters] = useState<Filters>({
    investmentStages: [],
    sectors: [],
    numInvestmentsRange: [0, 10000],
    numExitsRange: [0, 1000],
    hasIsraeliOffice: null,
    regions: [],
    investorTypes: [],
    employeeRange: [],
    operatingStatus: [],
    industries: [],
    numEmployeesRange: [],
    fundingRange: [0, 10000000000], // 10B USD
    countries: [],
    continents: [],
    founderSearch: '',
    investmentDateRange: ['', ''],
    onlyLeadInvestments: false,
    minExitValue: undefined,
    hasExits: false
  });

  useSearchTracking(searchTerm);

  useEffect(() => {
    fetch('/vc-data.json')
      .then(response => response.json())
      .then(data => {
        const sortedVCs = data.sort((a: VCData, b: VCData) =>
          a.name.toLowerCase().localeCompare(b.name.toLowerCase())
        );
        setVcData(sortedVCs);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error loading data:', err);
        setError('Failed to load VC data');
        setLoading(false);
      });
  }, []);

  // Track filter changes
  const handleFiltersChange = (newFilters: Filters) => {
    Object.keys(newFilters).forEach(key => {
      if (JSON.stringify(newFilters[key as keyof Filters]) !== JSON.stringify(filters[key as keyof Filters])) {
        trackFilterChange(key, newFilters[key as keyof Filters]);
      }
    });
    setFilters(newFilters);
  };

  const filteredData = vcData.filter(vc => {
    if (!vc || !vc.data || !vc.bdRaw) return false;

    const matchesSearch = !searchTerm ||
      vc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (vc.data.description || '').toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStages = filters.investmentStages.length === 0 ||
      (vc.bdRaw?.investment_stage && filters.investmentStages.some(stage =>
        vc.bdRaw.investment_stage.includes(stage)
      ));

    const matchesSectors = filters.sectors.length === 0 ||
      (vc.sectors && filters.sectors.some(sector =>
        vc.sectors?.includes(sector)
      ));

    const matchesInvestments =
      (!vc.bdRaw.num_investments && filters.numInvestmentsRange[0] === 0) ||
      (vc.bdRaw.num_investments >= filters.numInvestmentsRange[0] &&
        vc.bdRaw.num_investments <= filters.numInvestmentsRange[1]);

    const matchesExits =
      (!vc.bdRaw.num_exits && filters.numExitsRange[0] === 0) ||
      (vc.bdRaw.num_exits >= filters.numExitsRange[0] &&
        vc.bdRaw.num_exits <= filters.numExitsRange[1]);

    const matchesIsraeliOffice = filters.hasIsraeliOffice === null ||
      vc.hasIsraeliOffice === filters.hasIsraeliOffice;

    const matchesRegions = filters.regions.length === 0 ||
      (vc.data.region && filters.regions.includes(vc.data.region));

    const matchesInvestorType = filters.investorTypes.length === 0 ||
      (vc.bdRaw.investor_type && filters.investorTypes.includes(vc.bdRaw.investor_type));

    const matchesOperatingStatus = filters.operatingStatus.length === 0 ||
      (vc.bdRaw.operating_status && filters.operatingStatus.includes(vc.bdRaw.operating_status));

    const matchesIndustries = filters.industries.length === 0 ||
      (vc.bdRaw.industries && filters.industries.some(industry =>
        vc.bdRaw.industries?.some(i => i.value === industry)
      ));

    const matchesEmployeeRange = filters.numEmployeesRange.length === 0 ||
      (vc.bdRaw.num_employees && filters.numEmployeesRange.includes(vc.bdRaw.num_employees));

    const totalFunding = vc.bdRaw.funds_raised?.reduce((sum, fund) =>
      sum + (fund.money_raised || 0), 0) || 0;
    const matchesFunding = totalFunding >= filters.fundingRange[0] &&
      totalFunding <= filters.fundingRange[1];

    const matchesCountry = filters.countries.length === 0 ||
      (vc.data.country && filters.countries.includes(vc.data.country));

    const matchesContinent = filters.continents.length === 0 ||
      (vc.data.continent && filters.continents.includes(vc.data.continent));

    const matchesFounder = !filters.founderSearch || (
      vc.bdRaw.founders?.some(founder =>
        founder.value.toLowerCase().includes(filters.founderSearch.toLowerCase())
      ) || false
    );

    const matchesInvestmentDate = (
      !filters.investmentDateRange[0] && !filters.investmentDateRange[1]
    ) || (
        vc.bdRaw.investments?.some(investment => {
          const investmentDate = investment.announced_on;
          return (!filters.investmentDateRange[0] || investmentDate >= filters.investmentDateRange[0]) &&
            (!filters.investmentDateRange[1] || investmentDate <= filters.investmentDateRange[1]);
        }) || false
      );

    const matchesLeadInvestment = !filters.onlyLeadInvestments || (
      vc.bdRaw.investments?.some(investment => investment.lead_investor) || false
    );

    const matchesExitValue = !filters.minExitValue || (
      vc.bdRaw.exits?.some(exit => {
        const value = parseFloat(exit.value.replace(/[^0-9.-]+/g, ''));
        return !isNaN(value) && value >= (filters.minExitValue || 0);
      }) || false
    );

    const matchesHasExits = !filters.hasExits || (
      (vc.bdRaw.exits?.length || 0) > 0
    );

    return matchesSearch && matchesStages && matchesSectors &&
      matchesInvestments && matchesExits && matchesIsraeliOffice &&
      matchesRegions && matchesInvestorType && matchesOperatingStatus &&
      matchesIndustries && matchesEmployeeRange && matchesFunding &&
      matchesCountry && matchesContinent &&
      matchesFounder && matchesInvestmentDate && matchesLeadInvestment &&
      matchesExitValue && matchesHasExits;
  });

  // Extract filter options from data
  const allStages = Array.from(new Set(vcData.flatMap(vc =>
    vc.bdRaw?.investment_stage?.split(', ') || []
  )));
  const allIndustries = Array.from(new Set(vcData.flatMap(vc =>
    vc.bdRaw?.industries?.map(i => i.value) || []
  )));

  const maxInvestments = Math.max(...vcData.map(vc => vc.bdRaw?.num_investments || 0));
  const maxExits = Math.max(...vcData.map(vc => vc.bdRaw?.num_exits || 0));
  const maxFunding = Math.max(...vcData.map(vc =>
    vc.bdRaw?.funds_raised?.reduce((sum, fund) => sum + (fund.money_raised || 0), 0) || 0
  ));

  // Add analytics event tracking for VC clicks
  const handleVCClick = (vc: VCData) => {
    trackVCInteraction(vc.name);
    setSelectedVC(vc);
    setIsModalOpen(true);
    trackModalInteraction('open', vc.name);
  };

  const handleModalClose = () => {
    if (selectedVC) {
      trackModalInteraction('close', selectedVC.name);
    }
    setIsModalOpen(false);
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col font-['Inter']">
      <Header />

      {/* Mobile Hamburger Menu */}
      <button
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        className={`
          md:hidden fixed top-0 z-50 p-2
          transition-all duration-300 ease-in-out
          ${isSidebarOpen ? 'left-[20rem]' : 'left-4'}
          bg-white rounded-r-md hover:bg-white
        `}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className="w-5 h-5 text-gray-600"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d={isSidebarOpen
              ? "M6 18L18 6M6 6l12 12"
              : "M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"}
          />
        </svg>
      </button>

      <div className="flex flex-1">
        {/* Overlay for mobile */}
        {isSidebarOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
            onClick={() => setIsSidebarOpen(false)}
          />
        )}

        {/* Sidebar */}
        <div className={`
          fixed md:static md:block
          inset-y-0 left-0 z-40
          w-80 bg-white border-r border-gray-200
          transform transition-transform duration-300 ease-in-out
          ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
        `}>
          <div className="h-px bg-gray-200 w-full"></div>
          <div className="h-full md:h-[calc(100vh-120px)] overflow-y-auto">
            <FiltersSidebar
              filters={filters}
              onFiltersChange={handleFiltersChange}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              allStages={allStages}
              allIndustries={allIndustries}
              maxInvestments={maxInvestments || 1000}
              maxExits={maxExits || 100}
              maxFunding={maxFunding || 1000000000}
            />
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 bg-gray-50 w-full">
          <div className="h-[calc(100vh-116px)] overflow-y-auto">
            <Results
              filteredData={filteredData}
              onVCClick={handleVCClick}
            />
          </div>
        </div>
      </div>

      <VCModal
        vc={selectedVC}
        isOpen={isModalOpen}
        onClose={handleModalClose}
      />
    </div>
  );
}
